<!--
  COD1 · Abbonamento mensile partner — resoconto di periodo.

  SORGENTE: la tabella `fattura`. `createFattura()` scorre le sedi con
  `abbonamento > 0` e genera una riga per ciascuna:
    imponibile = headquarter.abbonamento
    importo    = 1,22 × abbonamento   (IVA)
  Una riga della tabella = il canone di UNA sede per il mese.

  L'id della rotta è quello del **partner**, non della sede: si vedono tutte le
  sedi con canone in un colpo solo.

  ⚠️ DUE AZIONI SEPARATE: «Salva» aggiorna gli importi e basta, «Segna
  liquidate» chiude la riga. Correggere un imponibile e dichiarare incassato
  il canone sono due decisioni contabili diverse.
-->
<template>
  <div
    v-loading="loading"
    class="c1"
  >
    <div class="c1__head">
      <button
        type="button"
        class="c1__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c1__title">
          <span class="c1__code">COD1</span> Abbonamento mensile
        </h1>
        <p class="c1__sub">
          {{ partnerName || 'Partner' }} — canone delle sedi
        </p>
      </div>
    </div>

    <div class="c1__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="c1__sp" />
      <el-input
        v-model="search"
        placeholder="Sede o riferimento…"
        clearable
        style="width: 240px"
      />
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Canoni del mese
        </div>
        <div class="kpi__num">
          {{ euro(kpi.total) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da liquidare
        </div>
        <div class="kpi__num">
          {{ euro(kpi.toPay) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Liquidato
        </div>
        <div class="kpi__num">
          {{ euro(kpi.paid) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Sedi con canone
        </div>
        <div class="kpi__num">
          {{ fatture.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="setSegment(s.key)"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        ref="tableRef"
        :data="filtered"
        style="width: 100%"
        row-key="id"
        :empty-text="segment === 'daliq' && countOf('liq')
          ? `Niente da liquidare: ${countOf('liq')} ${countOf('liq') === 1 ? 'canone già liquidato' : 'canoni già liquidati'}, nella scheda Liquidate`
          : 'Nessun canone per il periodo selezionato'"
        @selection-change="onSelectionChange"
      >
        <el-table-column
          v-if="segment === 'daliq'"
          type="selection"
          width="46"
        />

        <el-table-column
          label="Sede"
          min-width="190"
        >
          <template #default="scope">
            <div class="sede__nm">
              {{ scope.row.headquarter?.name || scope.row.ragione_sociale || '—' }}
            </div>
            <div class="sede__sub">
              {{ scope.row.headquarter?.city || '' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Periodo"
          min-width="170"
        >
          <template #default="scope">
            <span class="num">
              {{ fmtDate(scope.row.date_start) }} – {{ fmtDate(scope.row.date_end) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Imponibile"
          width="150"
          align="right"
        >
          <template #default="scope">
            <el-input
              v-if="editing[scope.row.id]"
              v-model.number="draft[scope.row.id].imponibile"
              size="small"
              type="number"
              step="0.01"
            />
            <span
              v-else
              class="num"
            >{{ euro(scope.row.imponibile) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Totale"
          width="150"
          align="right"
        >
          <template #default="scope">
            <el-input
              v-if="editing[scope.row.id]"
              v-model.number="draft[scope.row.id].importo"
              size="small"
              type="number"
              step="0.01"
            />
            <span
              v-else
              class="num val"
            >{{ euro(scope.row.importo) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Rif. fattura"
          width="150"
        >
          <template #default="scope">
            <el-input
              v-if="editing[scope.row.id]"
              v-model="draft[scope.row.id].rif_fattura"
              size="small"
            />
            <span
              v-else
              class="num"
            >{{ scope.row.rif_fattura || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="150"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.liquidato ? 'ok' : 'attn'"
              :label="scope.row.liquidato ? 'Liquidato' : 'Da liquidare'"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="170"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <template v-if="editing[scope.row.id]">
              <el-button
                size="small"
                @click="cancelEdit(scope.row)"
              >
                Annulla
              </el-button>
              <el-button
                size="small"
                type="primary"
                :loading="saving === scope.row.id"
                @click="saveRow(scope.row)"
              >
                Salva
              </el-button>
            </template>
            <el-button
              v-else
              size="small"
              @click="startEdit(scope.row)"
            >
              Modifica
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div
        v-if="segment === 'daliq'"
        class="bulk"
      >
        <span class="bulk__info">
          <b>{{ selected.length }}</b> selezionate · totale
          <b>{{ euro(selectedTotal) }}</b>
        </span>
        <span class="c1__sp" />
        <el-button
          type="success"
          :disabled="!selected.length"
          :loading="liquidating"
          @click="confirmLiquidate"
        >
          Segna liquidate
        </el-button>
      </div>

      <div class="board__foot">
        <span>{{ filtered.length }} canoni</span>
        <span class="c1__sp" />
        <span class="board__tot">
          Totale mostrato <b>{{ euro(filteredTotal) }}</b>
        </span>
      </div>
    </div>

    <p class="c1__note">
      L'imponibile di ogni riga nasce dall'abbonamento della sede, e il totale è
      l'imponibile più IVA. Correggerli qui non modifica l'abbonamento
      impostato sulla scheda del partner: cambia solo questa fattura.
    </p>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import partnerAPI from '../../../services/partnerAPI.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'daliq', label: 'Da liquidare' },
  { key: 'liq', label: 'Liquidate' },
];

export default {
  name: 'Cod1Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const fatture = ref([]);
    const loading = ref(false);
    const saving = ref('');
    const liquidating = ref(false);
    const search = ref('');
    const segment = ref('daliq');
    const selected = ref([]);
    const partnerName = ref('');
    const tableRef = ref(null);

    const editing = reactive({});
    const draft = reactive({});

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    const bySegment = (key) =>
      fatture.value.filter((f) => (key === 'liq' ? f.liquidato : !f.liquidato));

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter((f) => {
        if (!q) return true;
        const hay = `${f.headquarter?.name || ''} ${f.headquarter?.city || ''} ` +
          `${f.ragione_sociale || ''} ${f.rif_fattura || ''}`;
        return hay.toLowerCase().includes(q);
      });
    });

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, f) => s + Number(f.importo || 0), 0)
    );

    const selectedTotal = computed(() =>
      selected.value.reduce((s, f) => s + Number(f.importo || 0), 0)
    );

    const kpi = computed(() => {
      let total = 0;
      let paid = 0;
      let toPay = 0;
      for (const f of fatture.value) {
        const v = Number(f.importo || 0);
        total += v;
        if (f.liquidato) paid += v;
        else toPay += v;
      }
      return { total, paid, toPay };
    });

    const load = async () => {
      loading.value = true;
      selected.value = [];
      for (const k of Object.keys(editing)) delete editing[k];
      try {
        const res = await contabilitaAPI.cod1Rows(
          route.params.id, year.value, month.value
        );
        fatture.value = res.fatture;
      } catch (e) {
        console.error('Caricamento COD1 fallito:', e);
        fatture.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadPartner = async () => {
      try {
        const r = await partnerAPI.getPartnerDetail(route.params.id);
        partnerName.value = r.data?.company_name || '';
      } catch (e) {
        console.warn('Nome partner non disponibile:', e?.message);
      }
    };

    // --- modifica degli importi: NON tocca lo stato di liquidazione ---
    const startEdit = (row) => {
      draft[row.id] = {
        imponibile: Number(row.imponibile || 0),
        importo: Number(row.importo || 0),
        rif_fattura: row.rif_fattura || '',
      };
      editing[row.id] = true;
    };

    const cancelEdit = (row) => {
      delete editing[row.id];
      delete draft[row.id];
    };

    const saveRow = async (row) => {
      // `rif_fattura` è testo: «2026/0001» e «FT-0913» si
      // salvano come sono. Vuoto = nessun riferimento, cioè NULL: mandare
      // stringa vuota salverebbe un riferimento fatto di niente.
      const rif = String(draft[row.id].rif_fattura ?? '').trim();
      saving.value = row.id;
      try {
        await contabilitaAPI.updateFattura(row.id, {
          ...draft[row.id],
          rif_fattura: rif || null,
        });
        ElMessage.success('Fattura aggiornata');
        cancelEdit(row);
        await load();
      } catch (e) {
        console.error('Salvataggio fattura fallito:', e);
        ElMessage.error('Salvataggio non riuscito');
      } finally {
        saving.value = '';
      }
    };

    // --- liquidazione: azione separata, sulle righe selezionate ---
    const confirmLiquidate = async () => {
      try {
        await ElMessageBox.confirm(
          `${selected.value.length} canoni per ${euro(selectedTotal.value)} ` +
            'verranno segnati come liquidati.',
          'Confermi la liquidazione?',
          { confirmButtonText: 'Segna liquidate', cancelButtonText: 'Annulla' }
        );
      } catch {
        return;
      }

      liquidating.value = true;
      const res = await Promise.allSettled(
        selected.value.map((f) =>
          contabilitaAPI.updateFattura(f.id, { liquidato: true })
        )
      );
      liquidating.value = false;

      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(
          `Liquidati ${res.length - ko} canoni su ${res.length}: ${ko} non riusciti.`
        );
      } else {
        ElMessage.success(`${res.length} canoni segnati come liquidati`);
      }
      await load();
    };

    const setSegment = (key) => {
      segment.value = key;
      selected.value = [];
      tableRef.value?.clearSelection?.();
    };

    const onSelectionChange = (rows) => { selected.value = rows; };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

    const goBack = () => router.push('/contabilita');

    onMounted(() => { load(); loadPartner(); });

    return {
      year, month, fatture, loading, saving, liquidating, search, segment,
      selected, partnerName, tableRef, editing, draft,
      filtered, filteredTotal, selectedTotal, kpi, periodLabel, SEGMENTS,
      countOf, setSegment, onSelectionChange, startEdit, cancelEdit, saveRow,
      confirmLiquidate, shiftMonth, euro, fmtDate, goBack,
    };
  },
};
</script>

<style scoped>
.c1 { padding: 4px 0 32px; }
.c1__sp { flex: 1; }

.c1__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c1__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c1__back:hover { border-color: var(--brand-violet); }
.c1__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c1__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--state-ok);
  padding: 3px 8px; border-radius: 6px;
}
.c1__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c1__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.bulk {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}
.bulk__info { font-size: 12.5px; color: var(--text-secondary); }
.bulk__info b { font-variant-numeric: tabular-nums; color: var(--text-primary); }

.sede__nm { font-weight: 600; color: var(--text-primary); }
.sede__sub { color: var(--text-tertiary); font-size: 11.5px; }
.num { font-variant-numeric: tabular-nums; }
.val { font-weight: 700; color: var(--text-primary); }

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c1__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c1 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
